import React from "react";
import { graphql } from "gatsby";
import styled, { css } from "styled-components";
import { Parallax } from "react-scroll-parallax";
import Img from "gatsby-image";
import {
  Row,
  Col,
  Gap,
  TextNice as Text,
  Title,
  TitleNice,
  Section,
  Button,
  TechnologyTop,
  TechnologyTopVideo,
  Link,
  SEO,
} from "../components";
import { OrderedTextList } from "../components/TextList";
import { up, addResponsivity } from "../lib/styles";

const CONTENT_WIDTH = "1000px";

const TextContent = ({ children }) => (
  <Row justifyContent="center">
    <Col
      maxWidth={CONTENT_WIDTH}
      mobileMaxWidth="600px"
      bp="tablet"
      alignItems="center"
    >
      <Text textAlign="left" mobileTextAlign="left" noMargin>
        {children}
      </Text>
    </Col>
  </Row>
);
const TextContainer = ({ children }) => (
  <Row justifyContent="center">
    <Col maxWidth={CONTENT_WIDTH}>{children}</Col>
  </Row>
);

const ContentImage = ({ ...props }) => (
  <Section.Simple background="white">
    <Col width="100%" maxWidth={CONTENT_WIDTH} margin="auto">
      <Img {...props} />
    </Col>
  </Section.Simple>
);

const TopTitle = styled(Title.Thin).attrs({
  innerAs: "h1",
  bp: 710,
})`
  ${({ theme: { colors } }) => css`
    color: ${colors.textWhite};
    position: absolute;
    bottom: 30px;
    width: max-content;
    left: 50%;
    transform: translateX(-50%);
    text-transform: uppercase;

    max-width: 90vw;

    ${up("tablet")} {
      max-width: 100%;
      bottom: 60px;
    }
  `}
`;

const InlineHeader = TitleNice.InlineHeader;

// const HybridTitle = styled(Title.Thin).attrs({})`
//   text-transform: uppercase;
//   text-align: center;
//   margin: 80px auto 40px auto;
// `;
const HybridTitleComponent = (props) => (
  <Title.Big
    innerAs="h2"
    fontWeight="400"
    fontSize="38px"
    lineHeight="1.2"
    black
    textAlign="center"
    mobileTextAlign="left"
    margin="80px auto 40px auto"
    uppercase
    {...props}
  />
);

const HybridTitle = styled(Title.Thin).attrs({})`
  text-transform: uppercase;
  text-align: center;
  font-weight: 700;

  margin: 80px auto 24px auto;

  ${up("tablet")} {
    margin: 80px auto 40px auto;
  }
`;

// const HybridTitle = styled(UsesTitle).attrs({})`
//   margin: 40px auto 24px auto;

//   ${up("tablet")} {
//     margin: 80px auto 40px auto;
//   }
// `;

const GapM = () => <Gap gap="40px" mobileGap="40px" bp="tablet" />;
const GapL = () => <Gap gap="64px" mobileGap="40px" bp="tablet" />;

class Hybrid extends React.Component {
  render() {
    const {
      location: { pathname },
      data: {
        topImage,
        topMobileImage,
        battery1Image,
        battery2Image,
        battery3Image,
        flightRangeImage,
        flightRangeMobileImage,
        safImage,
        map1Image,
        map2Image,
        map3Image,
        energyImage,
      },
    } = this.props;

    return (
      <>
        <SEO
          title="Hybrid VTOL Propulsion & Efficiency | Zuri"
          description="Zuri Hybrid VTOL delivers 5x more energy than batteries, enabling up to 700 km of low-emission, sustainable travel with SAF-powered propulsion."
          pathname={pathname}
        />
        <Gap.Top />

        <Col width="100%" position="relative">
          <Col
            width="100%"
            position="relative"
            maxWidth="1800px"
            margin="auto"
            hideBelow={800}
          >
            <Img
              fluid={topImage.image.fluid}
              alt="Labeled schematic of the Zuri Hybrid VTOL powertrain, showing key components of the hybrid-electric system including turboshaft engine, generator, fuel tank, batteries, power distribution unit, electronic speed controllers (ESCs), and electric motors powering the rotors."
              loading="eager"
            />
          </Col>
          <Col width="100%" showBelow={800}>
            <Img
              fluid={topMobileImage.image.fluid}
              alt="Labeled schematic of the Zuri Hybrid VTOL powertrain, showing key components of the hybrid-electric system including turboshaft engine, generator, fuel tank, batteries, power distribution unit, electronic speed controllers (ESCs), and electric motors powering the rotors."
            />
          </Col>

          <TopTitle>Zuri Hybrid VTOL Propulsion System</TopTitle>
          <Gap gap="80px" />
        </Col>

        <Section>
          <GapM />
          <TextContainer>
            <Text noMargin>
              <InlineHeader>
                Hybrid Power System Efficiency.
              </InlineHeader>{" "}
              Since founding Zuri in 2017, we have always believed a&nbsp;hybrid
              powertrain is the best way to power VTOL aircraft. It allows us to
              provide a longer range while maintaining extremely low emissions.
            </Text>
          </TextContainer>

          <GapL />
        </Section>

        <ContentImage
          fluid={energyImage.image.fluid}
          alt="Bar chart comparing energy density of batteries versus the Zuri hybrid powertrain, showing 230–280 Wh/kg for the best batteries and 1360 Wh/kg for Zuri’s hybrid system—over five times more energy"
        />

        <Section>
          <GapL />
          <TextContainer>
            <Text noMargin>
              <InlineHeader>
                5x More Energy Than Batteries.
              </InlineHeader>{" "}
              Even the best batteries today have 5 times less energy than we can
              derive from a hybrid powertrain. And no, this isn’t going to
              change anytime soon. With the current progress in battery specific
              energy, it will still be 50-60 years before batteries catch up.
              The figure of 1360 Wh/kg shown above corresponds to a 225 kg
              turbogenerator producing 600&nbsp;kWh in 2 hours from 216 kg of
              fuel. That’s achievable TODAY.
            </Text>
          </TextContainer>

          <HybridTitle>Zuri Hybrid VTOL Usable Range</HybridTitle>
        </Section>

        <Section.Simple>
          <Row
            justifyContent="space-evenly"
            mobileJustifyContent="center"
            alignItems="flex-end"
            mobileAlignItems="center"
            width="100%"
            wrap="nowrap"
            margin="auto"
            mobileFlexDirection="column"
          >
            <Col width="100%">
              <Img
                fluid={map1Image.image.fluid}
                alt="Map showing the extended 700+ km range of Zuri Hybrid VTOL compared to eVTOL range in the Brussels region, covering parts of France, Germany, the Netherlands, and the UK."
              />
            </Col>
            <Gap gap="16px" mobileGap="8px" bp="tablet" />
            <Col width="100%">
              <Img
                fluid={map2Image.image.fluid}
                alt="Map showing Zuri Hybrid VTOL’s extended range from the Washington D.C. area compared to eVTOL range, with Zuri’s 700+ km radius reaching as far as the Great Lakes, New England, and southeastern U.S. states."
              />
            </Col>
            <Gap gap="16px" mobileGap="8px" bp="tablet" />
            <Col width="100%">
              <Img
                fluid={map3Image.image.fluid}
                alt="Map comparing eVTOL range and Zuri Hybrid VTOL range in the Gulf region, centered on Dubai and Abu Dhabi, with Zuri's 700+ km range reaching across multiple countries including Oman, Qatar, and Iran."
              />
            </Col>
          </Row>
        </Section.Simple>

        <Section>
          <GapL />
          <TextContainer>
            <Text noMargin>
              <InlineHeader>
                Usable Range & Reserves.
              </InlineHeader>{" "}
              While you may see some eVTOLs claiming a 240 km range, it's
              important to note that this is an absolute range. All aircraft
              (including current airplanes and helicopters) are required to
              maintain a mandatory reserve — either 30 minutes or 45 minutes
              (depending primarily on weather conditions). When the reserve is
              considered, an eVTOL has only a 100 km range with a 30-minute
              reserve. Thanks to its hybrid powertrain, Zuri has a range of
              almost 900 km, making it capable of 700 km long flights even with
              a 30-minute reserve.
            </Text>
          </TextContainer>
          <HybridTitle>Sustainable Hybrid Propulsion</HybridTitle>
        </Section>

        <ContentImage
          fluid={safImage.image.fluid}
          alt="Bar chart comparing passenger kilometer emissions across transportation modes, showing Zuri Hybrid VTOL with SAF at 46 g CO₂ eq—lower than electric cars, buses, eVTOLs, airliners, and helicopters."
        />

        <Section>
          <GapL />
          <TextContainer>
            <Text noMargin>
              <InlineHeader>
                Lower Emissions with Smarter Power.
              </InlineHeader>{" "}
              How can a hybrid VTOL have lower emissions than an all-electric
              VTOL? There are three reasons.
              <OrderedTextList>
                <li>
                  The main one is SAF — Sustainable Aviation Fuel. Using SAF in
                  a turbogenerator produces only 136 g CO₂ eq/kWh. Recharging an
                  eVTOL from the electricity grid produces 275 g CO₂ eq/kWh,
                  twice as much!
                </li>
                <li>
                  Zuri has at least three times smaller batteries, so the
                  manufacturing and recycling costs and emissions are much
                  smaller.
                </li>
                <li>
                  Since our flights are typically 10x longer and generally one
                  flight means one battery life cycle, we can travel 10x longer
                  distance before the battery needs replacing.
                </li>
              </OrderedTextList>
            </Text>
          </TextContainer>

          <HybridTitle>
            Optimized Range for Urban & Regional Air Mobility
          </HybridTitle>
        </Section>

        <Section.Simple background="white">
          <Col
            width="100%"
            margin="auto"
            hideBelow="tablet"
            maxWidth={CONTENT_WIDTH}
          >
            <Img
              fluid={flightRangeImage.image.fluid}
              alt="Graphic showing Zuri Hybrid VTOL’s operational focus on urban and regional air mobility in the 30 to 700+ km range, contrasting with hobby eVTOLs limited to under 30 km."
            />
          </Col>
          <Col width="100%" showBelow="tablet">
            <Img
              fluid={flightRangeMobileImage.image.fluid}
              alt="Graphic showing Zuri Hybrid VTOL’s operational focus on urban and regional air mobility in the 30 to 700+ km range, contrasting with hobby eVTOLs limited to under 30 km."
            />
          </Col>
        </Section.Simple>

        <Section>
          <GapL />
          <TextContainer>
            <Text noMargin>
              <InlineHeader>
                Optimized Flight Range for Air Mobility.
              </InlineHeader>{" "}
              Zuri integrates urban and regional air mobility by designing
              hybrid aircraft with optimized range capabilities, balancing VTOL
              for urban settings with extended flight capabilities for regional
              travel. Our approach includes efficient propulsion systems,
              aerodynamic design, and lightweight materials to maximize fuel
              efficiency and range. This allows for both short urban flights and
              longer regional trips, enhancing overall air mobility and
              connectivity.
            </Text>
          </TextContainer>

          <HybridTitle>Hybrid VTOL Battery Efficiency</HybridTitle>
        </Section>

        <Row
          justifyContent="space-evenly"
          mobileJustifyContent="center"
          alignItems="flex-end"
          mobileAlignItems="center"
          width="100%"
          maxWidth="1435px"
          margin="auto"
          wrap="nowrap"
          mobileFlexDirection="column"
        >
          <Col basis="674px" mobileBasis={1} width="100%">
            <Img
              fluid={battery1Image.image.fluid}
              alt="Concept image of a fully electric VTOL aircraft with highlighted battery cells representing 200–300 kWh capacity."
            />

            <Title.UsagePlaneType white>eVTOL</Title.UsagePlaneType>
            <Gap gap="4px" />
            <Title.UsagePlaneType white>200-300 kWh</Title.UsagePlaneType>
          </Col>
          <Col
            basis="736px"
            mobileBasis={1}
            width="100%"
            margin="0"
            mobileMargin="40px auto 0"
          >
            <Img
              fluid={battery2Image.image.fluid}
              alt="Zuri Hybrid VTOL aircraft with visualized 60 kWh battery layout, highlighting efficient energy use for medium-range travel."
            />
            <Title.UsagePlaneType white>Zuri</Title.UsagePlaneType>
            <Gap gap="4px" />
            <Title.UsagePlaneType white>60 kWh</Title.UsagePlaneType>
          </Col>
          <Col
            basis="510px"
            mobileBasis={1}
            mobileWidth="75%"
            margin="0"
            mobileMargin="40px auto 0"
          >
            <Img
              fluid={battery3Image.image.fluid}
              alt="Illustration of an electric car with visible battery pack layout showing 75–100 kWh energy storage."
            />
            <Col margin="0" mobileMargin="-40px 0 0 0" style={{ zIndex: 1 }}>
              <Title.UsagePlaneType white>Electric car</Title.UsagePlaneType>
              <Gap gap="4px" />
              <Title.UsagePlaneType white>75-100 kWh</Title.UsagePlaneType>
            </Col>
          </Col>
        </Row>
        <Gap gap="80px" mobileGap="40px" bp="tablet" />

        <Section>
          <GapL />

          <TextContainer>
            <Text noMargin>
              <InlineHeader>
                Lightweight & Efficient Battery.
              </InlineHeader>{" "}
              Hybrid power systems in aerospace applications outperform fully
              electric systems by integrating a generator set with electric
              propulsion. This reduces the need for a large battery, and the
              generator set can even recharge the battery during flight.
              Advanced thermal management in hybrids keeps batteries within
              ideal temperatures, enhancing longevity and reducing maintenance
              needs.
            </Text>
          </TextContainer>
          <Gap gap="120px" mobileGap="80px" bp="tablet" />
        </Section>
      </>
    );
  }
}

export default Hybrid;

export const pageQuery = graphql`
  fragment ContentImage on File {
    image: childImageSharp {
      fluid(maxWidth: 1150, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }

  fragment MapImage on File {
    image: childImageSharp {
      fluid(maxWidth: 629, quality: 80) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }

  query {
    topImage: file(
      relativePath: {
        eq: "images/hybrid/zuri-hybrid-vtol-powertrain-schematic-turboshaft-generator-battery-motor-esc.jpg"
      }
    ) {
      ...FullHdImage
    }
    topMobileImage: file(
      relativePath: {
        eq: "images/hybrid/zuri-hybrid-vtol-powertrain-schematic-turboshaft-generator-battery-motor-esc-mobile.jpg"
      }
    ) {
      image: childImageSharp {
        fluid(maxWidth: 800, quality: 80) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    map1Image: file(
      relativePath: {
        eq: "images/hybrid/zuri-hybrid-vtol-range-map-europe-evtol-comparison-brussels.jpg"
      }
    ) {
      ...MapImage
    }
    map2Image: file(
      relativePath: {
        eq: "images/hybrid/zuri-hybrid-vtol-range-map-usa-east-evtol-comparison-washington-dc.jpg"
      }
    ) {
      ...MapImage
    }
    map3Image: file(
      relativePath: {
        eq: "images/hybrid/zuri-hybrid-vtol-range-map-middle-east-evtol-comparison-dubai.jpg"
      }
    ) {
      ...MapImage
    }

    battery1Image: file(
      relativePath: {
        eq: "images/hybrid/evtol-battery-capacity-200-300kwh.jpg"
      }
    ) {
      image: childImageSharp {
        fluid(maxWidth: 674, quality: 80) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    battery2Image: file(
      relativePath: { eq: "images/hybrid/zuri-hybrid-vtol-battery-60kwh.jpg" }
    ) {
      image: childImageSharp {
        fluid(maxWidth: 736, quality: 80) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    battery3Image: file(
      relativePath: {
        eq: "images/hybrid/electric-car-battery-capacity-75-100kwh.jpg"
      }
    ) {
      image: childImageSharp {
        fluid(maxWidth: 510, quality: 80) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    flightRangeImage: file(
      relativePath: {
        eq: "images/hybrid/zuri-hybrid-vtol-range-segment-chart-urban-regional-air-mobility.jpg"
      }
    ) {
      ...ContentImage
    }
    flightRangeMobileImage: file(
      relativePath: {
        eq: "images/hybrid/zuri-hybrid-vtol-range-segment-chart-urban-regional-air-mobility-mobile.jpg"
      }
    ) {
      image: childImageSharp {
        fluid(maxWidth: 790, quality: 80) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    safImage: file(
      relativePath: {
        eq: "images/hybrid/zuri-hybrid-vtol-saf-emissions-comparison-vs-evtol-airliner-car.jpg"
      }
    ) {
      ...ContentImage
    }
    energyImage: file(
      relativePath: {
        eq: "images/hybrid/zuri-hybrid-vtol-energy-density-comparison-batteries-vs-hybrid-wh-per-kg.jpg"
      }
    ) {
      ...ContentImage
    }
  }
`;
